/** Journal slice — the reference jrnModel() ported verbatim (design-reference/PointFive OS Original.dc.html,
 *  file lines 5078–5475 / script lines 1052–1449). Annual calendar drilling year → month → week → day, day view with
 *  prep + post-session notes, mood, tags, linked trades and pre-entry / in-trade / post-trade screenshots.
 *  Authored days come from `c.data.journal.jrnDays` (JRN_DAYS);
 *  one dataset per date — REV_DAYS owns the three reviewed sessions, EX_TRADES every earlier date (via c.trades()).
 *  Keys the reference returns but the template never binds (absent from the generated ViewModel) are omitted as dead:
 *  jLevelIsMonth, jLevelIsWeek, jPrevMonth, jNextMonth, jScopeLabel, jCal, jWeekCal, jDows, jNoJournal, jDayTrades,
 *  jGroupBy, jSearch, setJSearch, jMoodDerived, jMoodChips, jIndex, jIndexCount. */
import type { ChangeEvent } from 'react';
import type { JournalDay } from '@pointfive/contracts';
import type { Ctx } from './ctx';
import type { ViewModel } from '@/screens/generated/view-model';

const M = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;
const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
const WDOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const;
const PLAYBOOK = ['London Open Reversal', 'Trend Continuation', 'Breakout Pullback'];
type ShotLabel = 'pre-entry' | 'in-trade' | 'post-trade' | 'other';
const SHOT_TONE: Record<ShotLabel, string> = {
  'pre-entry': 'var(--sky)',
  'in-trade': 'var(--amb)',
  'post-trade': 'var(--pos)',
  other: 'var(--muted)',
};
const SHOT_SEQ: readonly ShotLabel[] = ['pre-entry', 'in-trade', 'post-trade'];
const SHOT_ALL: readonly ShotLabel[] = ['pre-entry', 'in-trade', 'post-trade', 'other'];
const GROUPS: [string, string][] = [
  ['none', 'None'],
  ['session', 'Session'],
  ['strategy', 'Strategy'],
  ['period', 'Period'],
];
const DAY_MS = 86400000;
const PRESENT = Date.UTC(2026, 6, 24);

const sgn = (v: number): string =>
  (v >= 0 ? '+' : '−') +
  '$' +
  Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Reference jrnKey(off): the journal-day key for a day offset back from the present — note the zero-based month. */
export function jrnKey(off: number): string {
  const d = new Date(PRESENT - off * DAY_MS);
  return (
    d.getUTCFullYear() +
    '-' +
    String(d.getUTCMonth()).padStart(2, '0') +
    '-' +
    String(d.getUTCDate()).padStart(2, '0')
  );
}

interface JTrade {
  off: number;
  y: number;
  m: number;
  day: number;
  week: number;
  acct: string;
  sym: string;
  side: string;
  lots: number;
  pnl: number;
  strat: string;
  time: string;
}
interface Stat {
  n: number;
  w: number;
  pnl: number;
  wr: number;
}
/** An authored JRN_DAYS entry, or one derived from the session's trades (`derived: true`). */
type JEntry = JournalDay & { derived?: boolean };
interface WeekCell {
  inMonth: boolean;
  dow: string;
  day: string;
  label: string;
  hasTrades: boolean;
  pnl: string;
  pnlFg: string;
  labelFg: string;
  bg: string;
  title: string;
  go: (() => void) | undefined;
  disabled: boolean;
  cur: string;
  opacity: string;
}
interface WeekRow {
  wi: number;
  label: string;
  range: string;
  pnl: string;
  pnlFg: string;
  trades: string;
  wr: string;
  cells: WeekCell[];
}
interface DayRow {
  time: string;
  sym: string;
  side: string;
  strat: string;
  acct: string;
  lots: string;
  pnl: string;
  pnlFg: string;
  sideFg: string;
  sideBg: string;
  attached: boolean;
  attachLabel: string;
  attachIcon: string;
  attachBd: string;
  attachBg: string;
  attachFg: string;
  toggle: () => void;
}

export function buildJournal(c: Ctx) {
  const S = c.S;
  const JRN = c.data.journal.jrnDays;

  // one dataset per date — REV_DAYS owns the three reviewed sessions (the same
  // rows End of Day and Replay read); EX_TRADES owns every earlier date
  const stamp = (off: number) => {
    const d = new Date(PRESENT - off * DAY_MS);
    return {
      off,
      y: d.getUTCFullYear(),
      m: d.getUTCMonth(),
      day: d.getUTCDate(),
      week: Math.floor((d.getUTCDate() - 1) / 7),
    };
  };
  const trades: JTrade[] = c.trades().map((r) => ({
    ...stamp(r[1]),
    acct: r[2],
    sym: r[3],
    side: r[4],
    lots: r[5],
    pnl: r[8],
    strat: r[10],
    time: r[12],
  }));
  const inYear = trades.filter((t) => t.y === S.jYear);
  const inMonth = inYear.filter((t) => t.m === S.jMonth);
  const inWeek = S.jWeek === null ? inMonth : inMonth.filter((t) => t.week === S.jWeek);

  const stat = (arr: JTrade[]): Stat => {
    const w = arr.filter((t) => t.pnl > 0).length;
    const pnl = arr.reduce((a, t) => a + t.pnl, 0);
    return { n: arr.length, w, pnl, wr: arr.length ? (w / arr.length) * 100 : 0 };
  };

  const daysOfMonth = new Date(Date.UTC(S.jYear, S.jMonth + 1, 0)).getUTCDate();

  // ── year view: 12 month tiles
  const yearMonths = M.map((name, mi) => {
    const st = stat(inYear.filter((t) => t.m === mi));
    const entries = Object.keys(JRN).filter(
      (k) => k.indexOf(S.jYear + '-' + String(mi).padStart(2, '0')) === 0,
    ).length;
    const peak = Math.max(1, ...M.map((_, x) => Math.abs(stat(inYear.filter((t) => t.m === x)).pnl)));
    return {
      name: name.slice(0, 3),
      full: name,
      pnl: st.n ? sgn(st.pnl) : '—',
      pnlFg: st.n ? (st.pnl >= 0 ? 'var(--pos)' : 'var(--neg)') : 'var(--faint)',
      trades: st.n ? (st.n === 1 ? '1 trade' : st.n + ' trades') : 'No trades',
      entries: entries ? entries + (entries === 1 ? ' entry' : ' entries') : '',
      w: ((Math.abs(st.pnl) / peak) * 100).toFixed(1) + '%',
      bar: st.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
      bd: mi === S.jMonth ? 'color-mix(in srgb, var(--sky-fill) 45%, transparent)' : 'var(--card-line)',
      go: () => c.set({ jMonth: mi, jWeek: null, jLevel: 'year' }),
    };
  });

  // (the reference's month-view calendar grid — jCal/jWeekCal/jDows — is never bound by the template and is not built)

  // ── weekly heat map: real Mon–Fri calendar weeks intersecting the month
  const dayPnls: number[] = [];
  for (let d = 1; d <= daysOfMonth; d++) dayPnls.push(Math.abs(stat(inMonth.filter((t) => t.day === d)).pnl));
  const dayPeak = Math.max(1, ...dayPnls);
  const rampColor = (pnl: number, inten: number): string =>
    pnl >= 0
      ? 'color-mix(in srgb, var(--pos) ' + Math.round(10 + inten * 50) + '%, transparent)'
      : 'color-mix(in srgb, var(--neg) ' + Math.round(10 + inten * 50) + '%, transparent)';
  const monthStartMs = Date.UTC(S.jYear, S.jMonth, 1);
  const monthEndMs = Date.UTC(S.jYear, S.jMonth, daysOfMonth);
  const day1Dow = new Date(monthStartMs).getUTCDay();
  const mondayOffsetDays = day1Dow === 0 ? -6 : 1 - day1Dow;
  let cursorMs = monthStartMs + mondayOffsetDays * DAY_MS;
  const weeks: WeekRow[] = [];
  let wi = 0;
  while (cursorMs <= monthEndMs) {
    const cells: WeekCell[] = [];
    for (let i = 0; i < 5; i++) {
      const dt = new Date(cursorMs + i * DAY_MS);
      const inThisMonth = dt.getUTCMonth() === S.jMonth && dt.getUTCFullYear() === S.jYear;
      const d = dt.getUTCDate();
      const dayTrades = inThisMonth ? inMonth.filter((t) => t.day === d) : [];
      const st = stat(dayTrades);
      const inten = st.n ? Math.min(Math.abs(st.pnl) / dayPeak, 1) : 0;
      const off = Math.round((PRESENT - Date.UTC(S.jYear, S.jMonth, d)) / DAY_MS);
      const dow = WDOW[i] ?? '';
      cells.push({
        inMonth: inThisMonth,
        dow,
        day: String(d),
        label: dow + ' ' + d,
        hasTrades: st.n > 0,
        pnl: st.n ? sgn(st.pnl) : inThisMonth ? 'No trades' : '',
        pnlFg: !st.n ? 'var(--muted2)' : st.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
        labelFg: !inThisMonth ? 'var(--faint)' : 'var(--text-mid)',
        bg: !inThisMonth ? 'var(--l3)' : !st.n ? 'var(--l6)' : rampColor(st.pnl, inten),
        title: !inThisMonth
          ? ''
          : st.n
            ? (st.n === 1 ? '1 trade' : st.n + ' trades') +
              ' · ' +
              st.wr.toFixed(0) +
              '% win rate · ' +
              sgn(st.pnl)
            : 'No trades',
        // the reference binds `null` here; the cell is disabled and an absent handler is observably identical
        go:
          inThisMonth && st.n
            ? () => c.set({ jPick: off, jLevel: 'day', jYear: S.jYear, jMonth: S.jMonth })
            : undefined,
        disabled: !(inThisMonth && st.n),
        cur: inThisMonth && st.n ? 'pointer' : 'default',
        opacity: inThisMonth ? '1' : '0.35',
      });
    }
    const from = new Date(cursorMs).getUTCDate(),
      fromMonth = new Date(cursorMs).getUTCMonth();
    const to = new Date(cursorMs + 4 * DAY_MS).getUTCDate();
    const wTrades = cells
      .filter((x) => x.inMonth)
      .reduce<JTrade[]>((a, x) => a.concat(inMonth.filter((t) => t.day === +x.day)), []);
    const wst = stat(wTrades);
    const mon = M[S.jMonth] ?? '';
    weeks.push({
      wi: wi++,
      label: 'Week ' + (weeks.length + 1),
      range: (fromMonth === S.jMonth ? mon.slice(0, 3) + ' ' : mon.slice(0, 3) + ' ') + from + '–' + to,
      pnl: wst.n ? sgn(wst.pnl) : '—',
      pnlFg: wst.n ? (wst.pnl >= 0 ? 'var(--pos)' : 'var(--neg)') : 'var(--faint)',
      trades: wst.n === 1 ? '1 trade' : wst.n + ' trades',
      wr: wst.n ? wst.wr.toFixed(0) + '% win rate' : '—',
      cells,
    });
    cursorMs += 7 * DAY_MS;
  }

  // ── day view
  const pd = new Date(PRESENT - S.jPick * DAY_MS);
  const pdMonth = M[pd.getUTCMonth()] ?? '';
  const dayKey = jrnKey(S.jPick);
  const authored = JRN[dayKey];
  const sessionMood = (off: number): JEntry | null => {
    const day = trades.filter((t) => t.off === off);
    if (!day.length) return null;
    const st = stat(day);
    const offPlanDay = day.filter((t) => PLAYBOOK.indexOf(t.strat) < 0);
    const clean = offPlanDay.length === 0;
    let mood: string, energy: number;
    if (st.pnl > 0 && st.wr === 100 && clean) {
      mood = 'Sharp';
      energy = 8;
    } else if (st.pnl > 0 && clean) {
      mood = 'Focused';
      energy = 7;
    } else if (st.pnl > 0) {
      mood = 'Calm';
      energy = 6;
    } else if (st.pnl === 0) {
      mood = 'Flat';
      energy = 5;
    } else if (offPlanDay.length) {
      mood = 'Frustrated';
      energy = 4;
    } else {
      mood = 'Tired';
      energy = 4;
    }
    return {
      mood,
      energy,
      prep:
        'Armed ' +
        Array.from(new Set(day.map((t) => t.strat))).join(' and ') +
        '. Session window 07:00–16:00 London.',
      post:
        st.n +
        (st.n === 1 ? ' trade' : ' trades') +
        ' for ' +
        sgn(st.pnl) +
        ' at ' +
        st.wr.toFixed(0) +
        '% won' +
        (offPlanDay.length
          ? ' — ' + offPlanDay.length + ' entry outside the armed playbook.'
          : ' — every entry on plan.'),
      tags: clean ? ['Followed plan'] : ['Off-plan'],
      shots: 0,
      derived: true,
    };
  };
  const J: JEntry | null = authored || sessionMood(S.jPick);
  const dayTrades = trades.filter((t) => t.off === S.jPick);
  const dst = stat(dayTrades);
  const dayStrats: Record<string, { n: number; pnl: number }> = {};
  dayTrades.forEach((t) => {
    const e = dayStrats[t.strat] || { n: 0, pnl: 0 };
    dayStrats[t.strat] = e;
    e.n++;
    e.pnl += t.pnl;
  });

  const crumb: { label: string; go: () => void; on: boolean }[] = [
    { label: String(S.jYear), go: () => c.set({ jLevel: 'year', jWeek: null }), on: S.jLevel === 'year' },
  ];
  if (S.jLevel === 'day')
    crumb.push({ label: pdMonth.slice(0, 3) + ' ' + pd.getUTCDate(), go: () => {}, on: true });

  const yst = stat(inYear),
    mst = stat(inMonth),
    wst = stat(inWeek);

  const postNote = S.jPostEdit[dayKey] === undefined ? (J ? J.post : '') : S.jPostEdit[dayKey];

  // the reference caches this mapping on the instance (`__jDayTradesMapped`) and reads it back in jDayGroups
  const dayRows: DayRow[] = dayTrades.map((t) => {
    const attached = S.jAttach.indexOf(t.off + ':' + t.sym + ':' + t.time) >= 0;
    return {
      time: t.time,
      sym: t.sym,
      side: t.side,
      strat: t.strat,
      acct: t.acct,
      lots: t.lots.toFixed(2),
      pnl: sgn(t.pnl),
      pnlFg: t.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
      sideFg: t.side === 'BUY' ? 'var(--pos)' : 'var(--neg)',
      sideBg:
        t.side === 'BUY'
          ? 'color-mix(in srgb, var(--pos) 12%, transparent)'
          : 'color-mix(in srgb, var(--neg-fill) 14%, transparent)',
      attached,
      attachLabel: attached ? 'Attached' : 'Attach',
      attachIcon: attached ? 'ph-paperclip-horizontal' : 'ph-paperclip',
      attachBd: attached ? 'color-mix(in srgb, var(--sky-fill) 50%, transparent)' : 'var(--l10)',
      attachBg: attached ? 'color-mix(in srgb, var(--sky-fill) 12%, transparent)' : 'transparent',
      attachFg: attached ? 'var(--sky)' : 'var(--faint)',
      toggle: () => {
        const id = t.off + ':' + t.sym + ':' + t.time;
        const on = S.jAttach.indexOf(id) >= 0;
        c.set((s) => ({ jAttach: on ? s.jAttach.filter((x) => x !== id) : s.jAttach.concat(id) }));
        c.flash(
          on
            ? t.sym + ' detached from the entry'
            : t.sym + ' attached — chart screenshot pulled from the trade',
          on ? 'mut' : 'pos',
        );
      },
    };
  });

  const gb = S.jGroupBy || 'none';

  return {
    jCrumb: crumb.map((cr) =>
      Object.assign(cr, {
        fg: cr.on ? 'var(--text)' : 'var(--muted2)',
        fw: cr.on ? '600' : '500',
      }),
    ),
    jLevelIsYear: S.jLevel === 'year',
    jLevelIsDay: S.jLevel === 'day',
    jUp: () => c.set(() => ({ jLevel: 'year' })),
    jYearLabel: String(S.jYear),
    jMonthLabel: M[S.jMonth] + ' ' + S.jYear,
    jPrevYear: () => c.set((s) => ({ jYear: s.jYear - 1 })),
    jNextYear: () => c.set((s) => ({ jYear: s.jYear + 1 })),
    // scope summary
    jScopeStats: (() => {
      const st = S.jLevel === 'year' ? yst : S.jLevel === 'month' ? mst : S.jLevel === 'week' ? wst : dst;
      return [
        {
          k: 'Net P&L',
          v: st.n ? sgn(st.pnl) : '—',
          c: st.n ? (st.pnl >= 0 ? 'var(--pos)' : 'var(--neg)') : 'var(--muted2)',
        },
        { k: 'Trades', v: String(st.n), c: 'var(--text)' },
        { k: 'Win rate', v: st.n ? st.wr.toFixed(0) + '%' : '—', c: 'var(--text)' },
        {
          k: 'Journalled',
          v: (() => {
            const keys = Object.keys(JRN).filter((k) => {
              const p = k.split('-');
              if (+(p[0] ?? '') !== S.jYear) return false;
              if (S.jLevel === 'year') return true;
              if (+(p[1] ?? '') !== S.jMonth) return false;
              if (S.jLevel === 'month') return true;
              if (S.jLevel === 'week') return Math.floor((+(p[2] ?? '') - 1) / 7) === S.jWeek;
              return k === dayKey;
            });
            return String(keys.length);
          })(),
          c: 'var(--sky)',
        },
      ];
    })(),
    jYearMonths: yearMonths,
    jWeeks: weeks,
    // day detail
    jDayTitle: DOW[pd.getUTCDay()] + ', ' + pdMonth + ' ' + pd.getUTCDate() + ', ' + pd.getUTCFullYear(),
    jDayNet: dst.n ? sgn(dst.pnl) : '$0.00',
    jDayNetFg: dst.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
    jDayCount:
      dst.n === 0
        ? 'No trades'
        : (dst.n === 1 ? '1 trade' : dst.n + ' trades') + ' · ' + dst.wr.toFixed(0) + '% won',
    jHasJournal: !!J,
    jMood: J ? J.mood : '—',
    jEnergy: J ? 'Energy ' + J.energy + '/10' + (J.derived ? ' · read from the session' : '') : '',
    jEnergyW: J ? J.energy * 10 + '%' : '0%',
    jPrepNote: S.jPrepEdit[dayKey] === undefined ? (J ? J.prep : '') : S.jPrepEdit[dayKey],
    setJPrep: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const v = e.target.value;
      c.set((s) => ({ jPrepEdit: Object.assign({}, s.jPrepEdit, { [dayKey]: v }) }));
    },
    jPostNote: postNote,
    setJPost: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const v = e.target.value;
      c.set((s) => ({ jPostEdit: Object.assign({}, s.jPostEdit, { [dayKey]: v }) }));
    },
    jPostWords: (() => {
      const v = postNote;
      return v.trim() ? v.trim().split(/\s+/).length + ' words' : 'Not written yet';
    })(),
    jTagInput: S.jTagInput,
    setJTagInput: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      c.set({ jTagInput: e.target.value }),
    jAddTag: () => {
      const v = S.jTagInput.trim();
      if (!v) {
        c.flash('Type a tag first', 'neg');
        return;
      }
      c.set((s) => ({
        jTagAdd: Object.assign({}, s.jTagAdd, { [dayKey]: (s.jTagAdd[dayKey] || []).concat(v) }),
        jTagInput: '',
      }));
      c.flash('Tagged "' + v + '"', 'pos');
    },
    jDayTags: (J ? J.tags : []).concat(S.jTagAdd[dayKey] || []).map((t) => ({
      label: t,
      bd: /off-plan|revenge|low energy/i.test(t)
        ? 'color-mix(in srgb, var(--neg) 35%, transparent)'
        : 'var(--l10)',
      fg: /off-plan|revenge|low energy/i.test(t) ? 'var(--neg)' : 'var(--muted)',
    })),
    jDayHasTrades: dayTrades.length > 0,
    jDayNoTrades: dayTrades.length === 0,
    jGroupTabs: GROUPS.map(([id, label]) => ({
      label,
      go: () => c.set({ jGroupBy: id }),
      bg: gb === id ? 'var(--rail)' : 'transparent',
      fg: gb === id ? 'var(--rail-ink)' : 'var(--muted)',
      fw: gb === id ? '600' : '500',
    })),
    jDayGroups: (() => {
      const keyFn: (t: JTrade) => string =
        gb === 'session'
          ? (t) => {
              const hh = parseInt(String(t.time).slice(0, 2), 10);
              const mm = parseInt(String(t.time).slice(3, 5), 10);
              const mins = hh * 60 + mm;
              if (mins < 420) return 'Asia';
              if (mins < 780) return 'London';
              if (mins < 990) return 'London → NY overlap';
              return 'New York';
            }
          : gb === 'strategy'
            ? (t) => t.strat
            : gb === 'period'
              ? (t) => {
                  const hh = parseInt(String(t.time).slice(0, 2), 10);
                  return hh < 12 ? 'Morning' : hh < 17 ? 'Afternoon' : 'Evening';
                }
              : () => 'All trades';
      const order: string[] = [];
      const buckets: Record<string, DayRow[]> = {};
      dayTrades.forEach((t, i) => {
        const k = keyFn(t);
        const row = dayRows[i];
        if (!row) return; // dayRows mirrors dayTrades one-to-one
        const bucket = buckets[k] || [];
        if (!buckets[k]) {
          buckets[k] = bucket;
          order.push(k);
        }
        bucket.push(row);
      });
      return order.map((k) => {
        const rows = buckets[k] || [];
        const pnl = rows.reduce(
          (a, r) => a + (r.pnlFg === 'var(--pos)' ? 1 : -1) * parseFloat(r.pnl.replace(/[^0-9.]/g, '')),
          0,
        );
        return {
          label: k,
          n: rows.length === 1 ? '1 trade' : rows.length + ' trades',
          pnl: sgn(pnl),
          pnlFg: pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
          trades: rows,
        };
      });
    })(),
    jDayStrats: Object.entries(dayStrats).map(([k, v]) => ({
      label: k,
      n: v.n === 1 ? '1 trade' : v.n + ' trades',
      pnl: sgn(v.pnl),
      pnlFg: v.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
    })),
    jAttachCount: S.jAttach.length
      ? S.jAttach.length + (S.jAttach.length === 1 ? ' trade attached' : ' trades attached')
      : 'No trades attached',
    jShots: J
      ? Array.from({ length: J.shots }, (_, i) => {
          const label = SHOT_SEQ[i % 3] ?? 'pre-entry';
          return {
            name: (dayTrades[i % Math.max(1, dayTrades.length)] || { sym: 'Chart' }).sym + ' · ' + label,
            label,
            fg: SHOT_TONE[label],
            bd: 'color-mix(in srgb, ' + SHOT_TONE[label] + ' 30%, transparent)',
            bg: 'color-mix(in srgb, ' + SHOT_TONE[label] + ' 8%, transparent)',
          };
        })
      : [],
    jHasShots: !!J && J.shots > 0,
    jNoShots: !J || J.shots === 0,
    jShotCount: J ? String(J.shots) : '0',
    jShotLabels: SHOT_ALL.map((l) => ({
      label: l,
      on: S.jShot === l,
      go: () => c.set({ jShot: l }),
      bd: S.jShot === l ? 'color-mix(in srgb, ' + SHOT_TONE[l] + ' 50%, transparent)' : 'var(--l10)',
      bg: S.jShot === l ? 'color-mix(in srgb, ' + SHOT_TONE[l] + ' 10%, transparent)' : 'transparent',
      fg: S.jShot === l ? SHOT_TONE[l] : 'var(--muted)',
    })),
    jPasteShot: () =>
      c.flash('Screenshot pasted from clipboard as ' + S.jShot + ' — add a name to confirm', 'pos'),
    jGrabShots: () => {
      if (!S.jAttach.length) {
        c.flash('Attach a trade first — screenshots are pulled from the attached trades', 'neg');
        return;
      }
      c.flash(
        'Pulled ' +
          S.jAttach.length +
          ' chart screenshot' +
          (S.jAttach.length === 1 ? '' : 's') +
          ' from the attached trades',
        'pos',
      );
    },
    jSaveEntry: () => {
      const post = S.jPostEdit[dayKey] === undefined ? (J ? J.post : '') : S.jPostEdit[dayKey];
      if (!post.trim()) {
        c.flash('Write a post-session review before saving', 'neg');
        return;
      }
      c.flash('Journal entry saved for ' + pdMonth + ' ' + pd.getUTCDate(), 'pos');
    },
    // (the reference's journalled-day index — jSearch/setJSearch/jMoodDerived/jMoodChips/jIndex/jIndexCount — is never bound by the template)
  } satisfies Partial<ViewModel>;
}
export type JournalModel = ReturnType<typeof buildJournal>;
